import httpClient from "./http-client";
import { store } from "../store/store";
import { User } from "@auth0/auth0-react";

export default class SiteDocumentationService {
  private baseApiEndpoint = "/api/sdocs";

  constructor() {
    this.baseApiEndpoint =
      import.meta.env.VITE_BYPASS_PROXY === "true" ? "" : this.baseApiEndpoint;
  }

  private getUserGuid(): string {
    const user = store.getState().user.data;
    return user?.id || "";
  }

  private getSelectedSiteId(): string {
    const selection = store.getState().selection;
    return selection.selectedSiteId || "";
  }
  async getSiteDocumentation(
    page: number = 1, 
    pageSize: number = 100,
    sortBy?: string,
    sortOrder?: 'asc' | 'desc' | null
  ): Promise<any> {
    const userGuid = this.getUserGuid();
    const siteId = this.getSelectedSiteId();
    let response;

    try {
      const params: any = {
        user_id: userGuid,
        site_id: siteId,
        page: page,
        page_size: pageSize
      };

      // Backend expects single 'sort' parameter with format:
      // 'field_name' for ascending, '-field_name' for descending
      if (sortBy && sortOrder) {
        params.sort = sortOrder === 'desc' ? `-${sortBy}` : sortBy;
      }

      response = await httpClient.get(
        `${this.baseApiEndpoint}/site-docs?requestId=${new Date().getTime().toString()}`,
        { params },
      );
    } catch (error) {
      console.error("Error fetching site documentation:", error);
      throw error;
    }

    return response?.data || null;
  }

  async getDocumentTypes(): Promise<any> {
    const userGuid = this.getUserGuid();
    let response;

    try {
      response = await httpClient.get(
        `${this.baseApiEndpoint}/site-docs/types?user_id=${userGuid}&requestId=${new Date().getTime().toString()}`,
      );
    } catch (error) {
      console.error("Error fetching document types:", error);
      throw error;
    }

    return response?.data || null;
  }

  async getDocumentationById(documentId: string): Promise<any> {
    const userGuid = this.getUserGuid();
    let response;

    try {
      response = await httpClient.get(
        `${this.baseApiEndpoint}/site-docs/${documentId}?user_id=${userGuid}&requestId=${new Date().getTime().toString()}`,
      );
    } catch (error) {
      console.error("Error fetching documentation details:", error);
      throw error;
    }

    return response?.data || null;
  }

  async createDocumentation(data: FormData): Promise<any> {
    const userGuid = this.getUserGuid();
    const siteId = this.getSelectedSiteId();
    let response;

    try {
      response = await httpClient.post(
        `${this.baseApiEndpoint}/site-docs?user_id=${userGuid}&site_id=${siteId}`,
        data,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
    } catch (error) {
      console.error("Error creating documentation:", error);
      throw error;
    }

    return response?.data || null;
  }

  async updateDocumentation(documentId: string, data: FormData): Promise<any> {
    const userGuid = this.getUserGuid();
    const siteId = this.getSelectedSiteId();
    let response;

    try {
      response = await httpClient.put(
        `${this.baseApiEndpoint}/site-docs/${documentId}?user_id=${userGuid}&site_id=${siteId}`,
        data,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
    } catch (error) {
      console.error("Error updating documentation:", error);
      throw error;
    }

    return response?.data || null;
  }

  async deleteDocumentation(documentId: string): Promise<any> {
    const userGuid = this.getUserGuid();
    let response;

    try {
      response = await httpClient.delete(
        `${this.baseApiEndpoint}/site-docs/${documentId}?user_id=${userGuid}`
      );
    } catch (error) {
      console.error("Error deleting documentation:", error);
      throw error;
    }

    return response?.data || null;
  }

  async bulkDeleteDocumentation(documentIds: string[]): Promise<any> {
    const userGuid = this.getUserGuid();
    let response;

    try {
      response = await httpClient.post(
        `${this.baseApiEndpoint}/site-docs/bulk-delete?user_id=${userGuid}`,
        { document_ids: documentIds }
      );
    } catch (error) {
      console.error("Error bulk deleting documentation:", error);
      throw error;
    }

    return response?.data || null;
  }

  async downloadDocumentation(documentId: string): Promise<any> {
    const userGuid = this.getUserGuid();
    let response;

    try {
      response = await httpClient.get(
        `${this.baseApiEndpoint}/site-docs/${documentId}/download?user_id=${userGuid}`,
        { responseType: "blob" }
      );
    } catch (error) {
      console.error("Error downloading documentation:", error);
      throw error;
    }

    return response?.data || null;
  }
}
